import { useRef, useEffect, useState } from "react";
import LazyVideo from "@/components/LazyVideo";
import { CheckCircle2, Play } from "lucide-react";

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {const obs = new IntersectionObserver(([e]) => {if (e.isIntersecting) setInView(true);}, { threshold: 0.15 });if (ref.current) obs.observe(ref.current);return () => obs.disconnect();}, [ref]);
  return inView;
}

const VIDEO_URL = "/assets/video/padel-emocao.mp4";
const POSTER_IMG = "/assets/img/galeria-3.webp";

const benefits = [
"Fácil de aprender desde o primeiro jogo",
"Social e divertido — joga-se sempre em equipa",
"Mais ação e menos tempo de espera",
"As paredes fazem parte da estratégia e tornam cada ponto emocionante",
"Ideal para todas as idades e níveis de experiência"];


export default function WhatIsPadel() {
  const ref = useRef(null);
  const inView = useInView(ref);

  return (
    <section id="padel" data-testid="padel-section" ref={ref} className="py-24 bg-[#0d1320] overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        {/* Section Title */}
        <div className={`text-center mb-10 lg:mb-16 ${inView ? 'fade-in-up' : 'opacity-0'}`}>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white font-medium leading-tight">
            Padel não é <span className="text-jp-lime italic">Paddle!</span>
          </h2>
        </div>

        {/* Mobile: Video */}
        <div className={`lg:hidden mb-10 ${inView ? 'fade-in-up' : 'opacity-0'}`}>
          <div className="relative w-full">
            <div className="absolute -inset-4 bg-gradient-to-r from-jp-lime/20 via-transparent to-jp-lime/10 rounded-3xl blur-2xl opacity-50" />
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50 w-full">
              <div className="aspect-[9/16]">
                  <LazyVideo
                    src={VIDEO_URL}
                    poster={POSTER_IMG}
                    className="w-full h-full object-cover"
                    data-testid="padel-video-mobile" />

              </div>
            </div>
          </div>
          
          {/* Mobile CTA */}
          <div className="text-center mt-8">
            <p className="font-display text-xl text-white font-medium leading-snug mb-6">
              Fácil de aprender. <span className="text-jp-lime">Difícil de largar.</span>
            </p>
            <a
              href="#contacts"
              className="inline-flex items-center gap-2 bg-jp-lime text-black font-body font-semibold text-sm px-6 py-3.5 rounded-full hover:bg-jp-lime-dark transition-all group"
              data-testid="padel-cta-btn-mobile">

              Quero experimentar
              <span className="w-6 h-6 bg-black/10 rounded-full flex items-center justify-center group-hover:bg-black/20 transition-colors">
                <Play size={12} fill="currentColor" />
              </span>
            </a>
          </div>
        </div>

        {/* Desktop: Side by side layout with full content */}
        <div className={`hidden lg:grid lg:grid-cols-12 gap-16 items-center ${inView ? 'fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
          {/* Video - Vertical format on the left */}
          <div className="relative lg:col-span-5 flex justify-center">
            <div className="absolute -inset-4 bg-gradient-to-r from-jp-lime/20 via-transparent to-jp-lime/10 rounded-3xl blur-2xl opacity-50" />
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50 max-w-[320px] w-full">
              <div className="aspect-[9/16]">
                <LazyVideo
                    src={VIDEO_URL}
                  poster={POSTER_IMG}
                  className="w-full h-full object-cover"
                    data-testid="padel-video" />

              </div>
            </div>
          </div>

          {/* Content - on the right (full content for desktop) */}
          <div className="lg:col-span-7">
            <div className="space-y-6">
              <p className="font-body text-white/60 text-base leading-relaxed">O padel é o desporto que mais cresce em Portugal e no mundo — e basta experimentar uma vez para perceber porquê.

              </p>

              {/* Benefits list */}
              <div className="space-y-4 py-6">
                {benefits.map((item, i) =>
                <div
                  key={i}
                  className="flex items-start gap-3 group">

                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-jp-lime/20 flex items-center justify-center mt-0.5">
                      <CheckCircle2 size={14} className="text-jp-lime" />
                    </div>
                    <span className="font-body text-white/70 text-base leading-relaxed group-hover:text-white transition-colors">
                      {item}
                    </span>
                  </div>
                )}
              </div>

              {/* CTA text */}
              <div className="pt-4 border-t border-white/10">
                <p className="font-display text-xl sm:text-2xl text-white font-medium leading-snug">
                  Fácil de aprender. <span className="text-jp-lime">Difícil de largar.</span>
                </p>
              </div>

              {/* CTA Button */}
              <a
                href="#contacts"
                className="inline-flex items-center gap-2 bg-jp-lime text-black font-body font-semibold text-sm px-6 py-3.5 rounded-full hover:bg-jp-lime-dark transition-all group mt-4"
                data-testid="padel-cta-btn">

                Quero experimentar
                <span className="w-6 h-6 bg-black/10 rounded-full flex items-center justify-center group-hover:bg-black/20 transition-colors">
                  <Play size={12} fill="currentColor" />
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>);

}